import { useMemo, useState } from 'react'

import { useOpenWindow } from '@krp/client-runtime'
import {
  PLATFORM_WINDOWS,
  TwinScreen,
  buildTwinFamiliesVm,
  buildTwinRunsVm,
  calibrationExportUrl,
  defaultTwinRunId,
  holdFrame,
  isTwinRunActive,
  lastTestingCommand,
  platformTestingRuns,
  platformTextRuns,
  platformTwinAddFrame,
  platformTwinCancelRun,
  platformTwinFamilies,
  platformTwinRecompute,
  platformTwinRun,
  platformTwinRuns,
  platformTwinStartRun,
  releaseFrame,
  testingAccessOf,
  type TwinActions,
  type TwinPriority,
} from '@krp/platform-ui'

import { useAppSelector } from '../../app/hooks'
import { RUN_POLL_MS } from './timings'
import { useMyAccess } from './useMyAccess'

/** Сколько семейств сервер отдаёт за раз: первые — самые нужные (фото есть не у всех). */
const FAMILIES_PAGE = 100

const selectRuns = platformTwinRuns.select()
const selectPhotoRuns = platformTestingRuns.select()
const selectTextRuns = platformTextRuns.select()

/** «Похожие варианты» (RP-FB67): семейства двойников и кадры вариантов, замер и выгрузка для калибровки. */
export function TwinBinding() {
  const access = testingAccessOf(useMyAccess())
  const openWindow = useOpenWindow()
  const skip = access !== 'allowed'
  const [priority, setPriority] = useState<TwinPriority | null>(null)
  const [selected, setSelected] = useState<string | null>(null)

  const familiesArgs = useMemo(() => ({ priority, take: FAMILIES_PAGE }), [priority])
  const familiesSubscription = platformTwinFamilies.useQuerySubscription(familiesArgs, { skip })
  const families = useAppSelector(useMemo(() => platformTwinFamilies.select(familiesArgs), [familiesArgs]))

  const runs = useAppSelector(selectRuns)
  const active = (runs.data ?? []).some(isTwinRunActive)
  const runsSubscription = platformTwinRuns.useQuerySubscription(undefined, { skip, pollingInterval: active ? RUN_POLL_MS : 0 })
  // Очередь замеров общая: идущий замер по фото или по тексту тоже не даёт запустить свой — и опрашивается, пока не кончится.
  const photoActive = (useAppSelector(selectPhotoRuns).data ?? []).some((run) => run.state === 'queued' || run.state === 'running')
  const textActive = (useAppSelector(selectTextRuns).data ?? []).some((run) => run.state === 'queued' || run.state === 'running')
  platformTestingRuns.useQuerySubscription(undefined, { skip, pollingInterval: photoActive ? RUN_POLL_MS : 0 })
  platformTextRuns.useQuerySubscription(undefined, { skip, pollingInterval: textActive ? RUN_POLL_MS : 0 })

  const runId = defaultTwinRunId(runs.data ?? [], selected)
  const detailArgs = useMemo(() => ({ runId: runId ?? '' }), [runId])
  const detail = useAppSelector(useMemo(() => platformTwinRun.select(detailArgs), [detailArgs]))
  const detailActive = detail.data !== undefined && isTwinRunActive(detail.data.run)
  platformTwinRun.useQuerySubscription(detailArgs, { skip: skip || runId === null, pollingInterval: detailActive ? RUN_POLL_MS : 0 })

  const [addFrame, addResult] = platformTwinAddFrame.useMutation()
  const [recompute, recomputeResult] = platformTwinRecompute.useMutation()
  const [start, startResult] = platformTwinStartRun.useMutation()
  const [cancel, cancelResult] = platformTwinCancelRun.useMutation()

  const familiesVm = buildTwinFamiliesVm({ families, priority, command: lastTestingCommand([addResult, recomputeResult]) })
  const runsVm = buildTwinRunsVm({
    runs,
    detail: runId === null ? null : detail,
    otherActive: photoActive || textActive,
    command: lastTestingCommand([startResult, cancelResult]),
  })

  // Выгрузка — по разбираемому замеру, если он готов; иначе сервер сам возьмёт последний готовый.
  const exportRunId = (runs.data ?? []).find((run) => run.id === runId && run.state === 'done')?.id ?? null
  const refetchFamilies = familiesSubscription.refetch
  const refetchRuns = runsSubscription.refetch
  const actions = useMemo<TwinActions>(
    () => ({
      reload: () => {
        void refetchFamilies()
        void refetchRuns()
      },
      recompute: () => void recompute(familiesArgs),
      filter: setPriority,
      addFrame: async (draft) => {
        const frameId = holdFrame(draft.file)
        try {
          await addFrame({ catalogProductId: draft.catalogProductId, series: draft.series, frameId }).unwrap()
          return true
        } catch {
          // Причина отказа — в результате команды, её показывает экран.
          return false
        } finally {
          releaseFrame(frameId)
        }
      },
      startRun: (scope) =>
        void start({ scope })
          .unwrap()
          .then((run) => setSelected(run.id))
          .catch(() => undefined),
      cancelRun: (id) => void cancel({ runId: id }),
      selectRun: setSelected,
      download: (format) => {
        globalThis.open(calibrationExportUrl(format, exportRunId), '_blank', 'noopener')
      },
      openProducts: () => openWindow(PLATFORM_WINDOWS.testingProducts),
    }),
    [addFrame, cancel, exportRunId, familiesArgs, openWindow, recompute, refetchFamilies, refetchRuns, start],
  )
  return <TwinScreen access={access} families={familiesVm} runs={runsVm} actions={actions} />
}
